import React, { useState, useRef, useEffect } from 'react';
import { FaDownload, FaFilter, FaColumns, FaExpand, FaCompress, FaCaretDown, FaArrowsAltH, FaCalendarAlt, FaFileExport } from 'react-icons/fa';
import WebButton from '../webButton';

const ExcelToolbar = ({ 
  onExport, 
  onLocalExport,
  onToggleFilter, 
  onToggleColumns, 
  onToggleFullscreen,
  onAutoFitAll,
  onBulkAction,
  isFullscreen = false,
  showExport = true,
  showFilter = true,
  showColumns = true,
  showFullscreen = true,
  selectedCount = 0,
  bulkActions = [],
  title = "Data Table",
  leftActions = null,
  rightActions = null,
  customExportOptions = null,
  additionalExportOptions = null
}) => {
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showBulkMenu, setShowBulkMenu] = useState(false);
  const dropdownRef = useRef(null);
  const bulkDropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
      if (bulkDropdownRef.current && !bulkDropdownRef.current.contains(event.target)) {
        setShowBulkMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  return (
    <div className="excel-toolbar">
      <div className="excel-toolbar-left">
        <span className="excel-toolbar-title">{title}</span>
        {leftActions && (
          <div className="excel-toolbar-custom-left">
            {leftActions}
          </div>
        )}
        {selectedCount > 0 && (
          <div className="bulk-actions">
            <span className="selected-count">{selectedCount} selected</span>
            {bulkActions.length > 0 && (
              <>
                {/* Show direct bulk delete button if bulkDelete action exists */}
                {bulkActions.find(action => action.key === "bulkDelete") && (
                  <button 
                    className="bulk-delete-btn"
                    onClick={() => {
                      onBulkAction && onBulkAction("bulkDelete", selectedCount);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 12px",
                      fontSize: "12px",
                      height: "32px",
                      border: "1px solid #dc2626",
                      background: "#fff",
                      color: "#dc2626",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontWeight: 500,
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#dc2626";
                      e.currentTarget.style.color = "#fff";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#fff";
                      e.currentTarget.style.color = "#dc2626";
                    }}
                  >
                    <i className="fas fa-trash"></i>
                    Bulk Delete
                  </button>
                )}
                {/* Show dropdown for other bulk actions */}
                {bulkActions.filter(action => action.key !== "bulkDelete").length > 0 && (
                  <div className="bulk-dropdown" ref={bulkDropdownRef}>
                    <button 
                      className="bulk-action-btn"
                      onClick={() => setShowBulkMenu(!showBulkMenu)}
                    >
                      Actions <FaCaretDown />
                    </button>
                    {showBulkMenu && (
                      <div className="bulk-menu">
                        {bulkActions.filter(action => action.key !== "bulkDelete").map((action, index) => (
                          <button 
                            key={index}
                            className="bulk-menu-item"
                            onClick={() => {
                              onBulkAction && onBulkAction(action.key, selectedCount);
                              setShowBulkMenu(false);
                            }}
                          >
                            {action.icon && action.icon}
                            {action.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
      
      <div className="excel-toolbar-right">
        {rightActions && (
          <div className="excel-toolbar-custom-right">
            {rightActions}
          </div>
        )}
        {showFilter && (
          <button 
            className="excel-toolbar-btn" 
            onClick={onToggleFilter}
            title="Toggle Filters"
          >
            <FaFilter />
          </button>
        )}
        
        {showColumns && (
          <button 
            className="excel-toolbar-btn" 
            onClick={onToggleColumns}
            title="Column Settings"
          >
            <FaColumns />
          </button>
        )}

        <button 
          className="excel-toolbar-btn" 
          onClick={onAutoFitAll}
          title="Auto-fit all columns"
        >
          <FaArrowsAltH />
        </button>
        
        {showFullscreen && (
          <button 
            className="excel-toolbar-btn" 
            onClick={onToggleFullscreen}
            title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
          >
            {isFullscreen ? <FaCompress /> : <FaExpand />}
          </button>
        )}
        
        {showExport && (
          <div className="export-dropdown" ref={dropdownRef}>
            <button 
              className="excel-export-dropdown-btn"
              onClick={() => setShowExportMenu(!showExportMenu)}
            >
              <FaDownload />
              <span>Export</span>
              <FaCaretDown />
            </button>
            
            {showExportMenu && (
              <div className="export-menu">
                {customExportOptions ? (
                  <>
                    {customExportOptions.map((option, index) => (
                      <div key={index}>
                        {option.type === 'date-range' && option.dateRangeComponent}
                        {option.type === 'button' && (
                          <button 
                            className="export-menu-item"
                            onClick={() => {
                              option.onClick && option.onClick();
                              setShowExportMenu(false);
                            }}
                            disabled={option.disabled}
                            style={option.style || {}}
                          >
                            {option.icon && option.icon}
                            {option.label}
                          </button>
                        )}
                        {option.type === 'custom' && option.component}
                        {option.type === 'divider' && <div className="export-menu-divider" />}
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    <button 
                      className="export-menu-item"
                      onClick={() => {
                        onLocalExport && onLocalExport();
                        setShowExportMenu(false);
                      }}
                    >
                      Export Current View
                    </button>
                    <button 
                      className="export-menu-item"
                      onClick={() => {
                        onExport && onExport();
                        setShowExportMenu(false);
                      }}
                    >
                      Export All Data
                    </button>
                    {additionalExportOptions && additionalExportOptions.length > 0 && (
                      <>
                        <div className="export-menu-divider" />
                        {additionalExportOptions.map((option, index) => (
                          <button 
                            key={index}
                            className="export-menu-item"
                            onClick={() => {
                              option.onClick && option.onClick();
                              setShowExportMenu(false);
                            }}
                            disabled={option.disabled}
                            style={option.style || {}}
                          >
                            {option.icon && option.icon}
                            {option.label}
                          </button>
                        ))}
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExcelToolbar;